import { businessShieldCreditsReducer, State as BusinessShieldCreditsState } from './slices/businessShieldCredits';
import { businessShieldSSOReducer, State as BusinessShieldSSOState } from './slices/businessShieldSSO';
import { certificationsReducer, State as CertificationsState } from './slices/certifications';
import { certificationDocumentsReducer, State as CertificationDocumentsState } from './slices/certificationDocuments';
import { clientsForScoresReducer, State as ClientsForScoresState } from './slices/clientsForScores';
import { combineReducers } from 'redux';
import { commonRootReducer, CommonRootState } from '@pec/aion-ui-core/combineReducers';
import { drawerReducer, State as DrawerState } from './slices/drawer';
import { insurancesV2Reducer, State as InsurancesV2State } from './slices/insurancesV2';
import { ngqeClientsReducer, State as NgqeClientsState } from './slices/ngqeClients';
import { onlineTrainingCreditsReducer, State as OnlineTrainingCreditsState } from './slices/onlineTrainingCredits';
import { onlineTrainingSSOReducer, State as OnlineTrainingSSOState } from './slices/onlineTrainingSSO';
import {
  organizationInsuranceSectionsV2Reducer,
  State as OrganizationInsuranceSectionsV2State
} from './slices/organizationInsuranceSectionsV2';
import { organizationMetadataReducer, State as OrganizationMetadataState } from './slices/organizationMetadata';
import { programReviewsReducer, State as ProgramReviewsState } from './slices/programReviews';
import { publicKeysReducer, State as PublicKeysState } from '@pec/aion-ui-core/slices/publicKeys';
import { questionsReducer, State as QuestionsState } from './slices/questions';
import { reducer as releases, State as ReleasesState } from 'reducers/releases';
import { reducer as insurances, State as InsurancesState } from 'reducers/insurances';
import { reducer as predictiveRanking, State as PredictiveRankingState } from 'reducers/predictiveRanking';
import { reducer as options, State as OptionsState } from 'reducers/options';
import { reducer as scoreResults, State as ScoreResultsState } from 'reducers/scoreResults';
import { reducer as verifications, State as VerificationsState } from 'reducers/verifications';
import { reducer as paymentMethods, State as PaymentMethodsState } from 'reducers/paymentMethods';
import { reducer as autoPay, State as AutoPayState } from 'reducers/autoPay';
import { reducer as organizationDetails, State as OrganizationDetailsState } from 'reducers/organizationDetails';
import { reducer as connections, State as ConnectionsState } from 'reducers/connections';
import { reducer as autoAccept, State as AutoAcceptState } from 'reducers/autoAccept';
import { reducer as companyContact, State as CompanyContactState } from 'reducers/companyContact';
import { reducer as zuora, State as ZuoraState } from 'reducers/zuora';
import { reducer as billingErrors, State as BillingErrorsState } from 'reducers/billingErrors';
import { reducer as clientRankings, State as ClientRankingsState } from 'reducers/clientRankings';
import {
  reducer as releasedClientsWithDashboard,
  State as ReleasedClientsWithDashboardState
} from 'reducers/releasedClientsWithDashboard';
import { RootActions } from 'combineActions';
import { sectionsReducer, State as SectionsState } from './slices/sections';
import { segmentStatusesReducer, State as SegmentStatusesState } from './slices/segmentStatuses';
import { State as SubscriptionsV2State, subscriptionsV2Reducer } from './slices/subscriptionsV2';
import { State as ThirdPartyProductsState, thirdPartyProductsReducer } from './slices/thirdPartyProducts';
import { State as DataSharingConsentState, dataSharingConsentReducer } from './slices/dataSharingConsent';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';

export type SubscriberRootState = {
  predictiveRanking: PredictiveRankingState;
  insurances: InsurancesState;
  insurancesV2: InsurancesV2State;
  releases: ReleasesState;
  scoreResults: ScoreResultsState;
  verifications: VerificationsState;
  options: OptionsState;
  paymentMethods: PaymentMethodsState;
  autoPay: AutoPayState;
  organizationDetails: OrganizationDetailsState;
  connections: ConnectionsState;
  autoAccept: AutoAcceptState;
  companyContact: CompanyContactState;
  zuora: ZuoraState;
  billingErrors: BillingErrorsState;
  releasedClientsWithDashboard: ReleasedClientsWithDashboardState;
  clientRankings: ClientRankingsState;
  certifications: CertificationsState;
  certificationDocuments: CertificationDocumentsState;
  sections: SectionsState;
  segmentStatuses: SegmentStatusesState;
  subscriptionsV2: SubscriptionsV2State;
  organizationMetadata: OrganizationMetadataState;
  organizationInsuranceSectionsV2: OrganizationInsuranceSectionsV2State;
  thirdPartyProducts: ThirdPartyProductsState;
  onlineTrainingSSO: OnlineTrainingSSOState;
  businessShieldSSO: BusinessShieldSSOState;
  onlineTrainingCredits: OnlineTrainingCreditsState;
  businessShieldCredits: BusinessShieldCreditsState;
  drawer: DrawerState;
  questions: QuestionsState;
  ngqeClients: NgqeClientsState;
  publicKeys: PublicKeysState;
  clientsForScores: ClientsForScoresState;
  dataSharingConsent: DataSharingConsentState;
  programReviews: ProgramReviewsState;
};

export type RootState = SubscriberRootState & CommonRootState;

export const rootReducer = combineReducers<RootState, RootActions>({
  ...commonRootReducer,
  predictiveRanking,
  insurances,
  insurancesV2: insurancesV2Reducer,
  releases,
  scoreResults,
  verifications,
  options,
  paymentMethods,
  autoPay,
  organizationDetails,
  connections,
  autoAccept,
  companyContact,
  zuora,
  billingErrors,
  releasedClientsWithDashboard,
  clientRankings,
  certifications: certificationsReducer,
  certificationDocuments: certificationDocumentsReducer,
  sections: sectionsReducer,
  segmentStatuses: segmentStatusesReducer,
  subscriptionsV2: subscriptionsV2Reducer,
  organizationInsuranceSectionsV2: organizationInsuranceSectionsV2Reducer,
  organizationMetadata: organizationMetadataReducer,
  thirdPartyProducts: thirdPartyProductsReducer,
  onlineTrainingSSO: onlineTrainingSSOReducer,
  businessShieldSSO: businessShieldSSOReducer,
  onlineTrainingCredits: onlineTrainingCreditsReducer,
  businessShieldCredits: businessShieldCreditsReducer,
  drawer: drawerReducer,
  questions: questionsReducer,
  ngqeClients: ngqeClientsReducer,
  publicKeys: publicKeysReducer,
  clientsForScores: clientsForScoresReducer,
  dataSharingConsent: dataSharingConsentReducer,
  programReviews: programReviewsReducer
});

export const useTypedSelector: TypedUseSelectorHook<RootState> = useSelector;
export const useAppDispatch = () => useDispatch();
